// CRM 2.0 on / off switch on Project › Settings (M1-F5). One toggle in the
// same style as the tab's other toggles; nothing else on the tab changes.
// Switching saves at once through the server (health check + audit row); it is
// not part of the tab's Save button. If the health check finds a problem, a
// pop-up lists what to fix. Shown only with the permission
// Project › View Project Details › Settings › CRM 2.0 Switch (or Super Admin).
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import CustomSwitch from "../../../Subcomponents/CustomSwitch";
import ConfirmationAlert from "../../../Subcomponents/ConfirmationAlert";
import { errorTost, successTost } from "../../../Services/Toast";
import { getProjectSwitch, setProjectSwitch } from "../../../Services/Crm2ProjectSwitch";

function Crm2ProjectSwitch({ projectId, wrapperClassName, labelClassName, onChanged = () => {} }) {
  const [state, setState] = useState(null); // { isCrm2, crm2SwitchedAt }
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // true = turn on, false = turn off
  const [problems, setProblems] = useState(null); // checks that blocked switch-on
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    try {
      setState(await getProjectSwitch(projectId));
      setLoadError(null);
    } catch (err) {
      const status = err?.response?.status;
      console.error("[crm2][Crm2ProjectSwitch] load failed", { projectId, status, message: err?.response?.data?.message || err.message, url: err?.config?.url });
      setLoadError(
        !status ? "Could not reach the server" : status === 401 ? "Please log in again" : status === 403 ? "No permission to switch" : err?.response?.data?.message || "Could not load"
      );
    }
  };

  useEffect(() => {
    if (projectId) load();
  }, [projectId]);

  const doSwitch = async (on) => {
    setBusy(true);
    try {
      const res = await setProjectSwitch(projectId, on);
      console.log("[crm2][Crm2ProjectSwitch] switched", { projectId, on });
      successTost(on ? "CRM 2.0 turned on for new bookings" : "CRM 2.0 turned off for new bookings");
      setState({ isCrm2: res.isCrm2, crm2SwitchedAt: res.crm2SwitchedAt });
      onChanged(res.isCrm2, res.crm2SwitchedAt);
    } catch (err) {
      const body = err?.response?.data;
      console.warn("[crm2][Crm2ProjectSwitch] switch refused or failed", { projectId, on, status: err?.response?.status, message: body?.message || err.message });
      const blocked = (body?.health?.checks || []).filter((c) => c.status === "PROBLEM" || c.status === "ERROR");
      if (blocked.length) setProblems(blocked);
      else errorTost(body?.message || "Could not change the CRM 2.0 switch.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={wrapperClassName}>
      <div className={labelClassName} style={{ marginBottom: "1rem" }}>
        CRM 2.0
      </div>
      <CustomSwitch checked={!!state?.isCrm2} disabled={busy || !state} onChange={(e) => setConfirm(e.target.checked)} />
      {loadError && <div style={{ fontSize: "1.2rem", color: "#B42318", marginTop: "0.4rem" }}>{loadError}</div>}
      {state?.isCrm2 && state.crm2SwitchedAt && (
        <div style={{ fontSize: "1.2rem", color: "#667085", marginTop: "0.4rem" }}>
          On since {dayjs(Number(state.crm2SwitchedAt)).format("DD MMM YYYY")}
        </div>
      )}

      {confirm !== null && (
        <ConfirmationAlert
          open={confirm !== null}
          close={() => setConfirm(null)}
          buttonTitle={"Cancel"}
          title={confirm ? "Turn CRM 2.0 on?" : "Turn CRM 2.0 off?"}
          subTitle={
            confirm
              ? "New bookings of this project will run on CRM 2.0. Bookings that started before today finish on today's flow."
              : "New bookings will use today's flow. Bookings already on CRM 2.0 continue on CRM 2.0."
          }
          returnRespButton={confirm ? "Turn on" : "Turn off"}
          confirm={(value) => {
            const on = confirm;
            setConfirm(null);
            if (value === "confirmed") doSwitch(on);
          }}
        />
      )}

      {problems && (
        <ConfirmationAlert
          open={!!problems}
          close={() => setProblems(null)}
          title={"CRM 2.0 cannot be switched on yet"}
          subTitle={
            <div style={{ textAlign: "left" }}>
              <p style={{ margin: "0 0 0.8rem" }}>Fix these first:</p>
              {problems.map((c) => (
                <p key={c.code} style={{ margin: "0 0 0.6rem" }}>
                  • <b>{c.label}</b>: {c.detail} ({c.fixIn})
                </p>
              ))}
            </div>
          }
          returnRespButton={"OK"}
          confirm={() => setProblems(null)}
        />
      )}
    </div>
  );
}

export default Crm2ProjectSwitch;
